import { useCallback, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Button } from '@follow/ui';

import AssetsService from '../../services/AssetsService';
import SubscriptionService from '../../services/SubscriptionService';
import UserService from '../../services/UserService';
import { Station } from '../../services/models/assets';
import { UserRole } from '../../services/models/user';

import StationHeader from './components/StationHeader';
import DateRangeFilter from './components/DateRangeFilter';
import ApiUsageChart from './components/ApiUsageChart';
import LockUsageChart from './components/LockUsageChart';
import ModalRebootStation from '../../components/Modals/ModalRebootStation';
import useLocationRole from '../../hooks/useLocationRole';

type RangeKey = '1d' | '7d' | '30d' | '90d';

const rangeToDays: Record<RangeKey, number> = {
  '1d': 1,
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

const StationOverview = () => {
  const { siteId, stationId } = useParams<{ siteId: string; stationId: string }>();

  // This page is built from /v1/subscriptions/usage and /lock-usage, which are
  // owner/admin-only, so a teammate would land on an empty screen. Operations is
  // their first useful tab. Wait for the access block before deciding, otherwise
  // an owner briefly looks like a teammate and gets bounced.
  const { canManage, isLoading: roleLoading } = useLocationRole(siteId);

  // Date range state
  const [selectedRange, setSelectedRange] = useState<RangeKey>('30d');
  const [customRange, setCustomRange] = useState<{ start: Date; end: Date } | null>(null);

  const { startDate, endDate } = useMemo(() => {
    if (customRange) {
      return { startDate: customRange.start, endDate: customRange.end };
    }
    const end = new Date();
    end.setSeconds(0, 0);
    const start = new Date();
    start.setDate(start.getDate() - rangeToDays[selectedRange]);
    start.setSeconds(0, 0);
    return { startDate: start, endDate: end };
  }, [selectedRange, customRange]);

  const handleRangeChange = useCallback((range: RangeKey) => {
    setSelectedRange(range);
    setCustomRange(null);
  }, []);

  const handleCustomRangeApply = useCallback((start: Date, end: Date) => {
    setCustomRange({ start, end });
  }, []);

  // Modal state
  const [rebootModalOpen, setRebootModalOpen] = useState(false);

  const { data: currentUser } = useQuery({
    queryKey: ['user'],
    queryFn: () => UserService.getUser(),
    select: (data) => data.data,
  });
  const isAdmin = currentUser?.role === UserRole.ADMIN;
  const canReboot = isAdmin || currentUser?.role === UserRole.OWNER;

  const effectiveRange = useMemo((): RangeKey => {
    if (!customRange) return selectedRange;
    const days = Math.ceil((customRange.end.getTime() - customRange.start.getTime()) / 86400000);
    if (days <= 1) return '1d';
    if (days <= 7) return '7d';
    if (days <= 30) return '30d';
    return '90d';
  }, [customRange, selectedRange]);

  // Station data
  const { data: station, isError } = useQuery({
    queryKey: ['stations', siteId, stationId],
    queryFn: () => AssetsService.getStations(Number(siteId!)),
    select: (data): Station | undefined =>
      data.stations.find((s) => s.id === Number(stationId)),
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
  });

  // Usage stats
  const rangeQueryKey = customRange
    ? `custom-${startDate.toISOString()}-${endDate.toISOString()}`
    : selectedRange;

  const { data: usageStats, isLoading: usageLoading } = useQuery({
    queryKey: ['station-usage', station?.token, rangeQueryKey],
    queryFn: () =>
      SubscriptionService.getUsageStats(
        effectiveRange,
        startDate.toISOString(),
        station!.token,
        customRange ? endDate.toISOString() : undefined,
      ),
    // Skipped for a teammate: these endpoints are owner/admin-only and would
    // just 403 in the moment before the redirect below fires.
    enabled: !!station?.token && canManage,
  });

  // Lock usage stats
  const { data: lockUsageStats, isLoading: lockUsageLoading } = useQuery({
    queryKey: ['station-lock-usage', station?.id, rangeQueryKey],
    queryFn: () =>
      SubscriptionService.getLockUsageStats(
        effectiveRange,
        startDate.toISOString(),
        station!.id,
        customRange ? endDate.toISOString() : undefined,
      ),
    enabled: !!station?.id && canManage,
  });

  // Mutations
  const rebootMutation = useMutation({
    mutationFn: (token: string) => AssetsService.rebootStation(token),
    onSuccess: () => {
      toast.success('Redémarrage envoyé');
    },
    onError: () => {
      toast.error('Erreur lors du redémarrage');
    },
    onSettled: () => setRebootModalOpen(false),
  });

  // Placed after every hook so the hook order stays stable across renders.
  if (!roleLoading && !canManage) {
    return <Navigate to={`/site/${siteId}/station/${stationId}/operations`} replace />;
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="text-error-600 typo-small">Erreur lors du chargement de la station.</span>
      </div>
    );
  }

  if (!station) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="text-neutral-40 typo-small">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1280px] px-4 flex flex-col gap-4 pb-8">
      {/* Header */}
      <StationHeader
        station={station}
        isAdmin={isAdmin}
        action={canReboot && (
          <Button variant="outline" size="sm" onClick={() => setRebootModalOpen(true)}>
            <ArrowPathIcon className="w-4 h-4 mr-1.5" />
            Redémarrer
          </Button>
        )}
      />

      {/* Date range filter */}
      <DateRangeFilter
        selectedRange={selectedRange}
        onRangeChange={handleRangeChange}
        startDate={startDate}
        endDate={endDate}
        isCustomRange={customRange !== null}
        onCustomRangeApply={handleCustomRangeApply}
      />

      {/* Charts */}
      <div className="flex flex-col md:flex-row gap-4">
        <ApiUsageChart
          data={usageStats?.data ?? []}
          total={usageStats?.total ?? 0}
          maxRequests={usageStats?.billing?.quotaLimit ?? null}
          isLoading={usageLoading}
          range={effectiveRange}
          startDate={startDate}
          endDate={endDate}
        />
        <LockUsageChart ecus={station.ecus || []} lockUsageData={lockUsageStats?.data ?? []} isLoading={lockUsageLoading} />
      </div>

      {/* Modals */}
      <ModalRebootStation
        visible={rebootModalOpen}
        stationName={station.name || `Station ${station.id}`}
        onCancel={() => setRebootModalOpen(false)}
        onConfirm={() => rebootMutation.mutate(station.token)}
        isLoading={rebootMutation.isPending}
      />
    </div>
  );
};

export default StationOverview;
